import { registerLocaleData } from '@angular/common';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  ErrorHandler,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import {
  DownloadOutline,
  LoadingOutline,
  PlusOutline,
  AppstoreOutline,
} from '@ant-design/icons-angular/icons';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule, Storage } from '@ionic/storage-angular';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import { TraceService, createErrorHandler } from '@sentry/angular';
import { AbsenceCategoriesState } from '@wilson/absences';
import { AccountModule, UsersState } from '@wilson/account';
import { ActivitiesModule } from '@wilson/activities';
import { AssignmentModule } from '@wilson/assignment';
import { AuthCoreModule, AuthInterceptor, AuthState } from '@wilson/auth/core';
import {
  AuthorizationModule,
  PermissionInterceptor,
} from '@wilson/authorization';
import { BankDetailsState } from '@wilson/bank-details';
import { ClientsState, ExternalUsersState } from '@wilson/clients/states';
import { ConfigModule, ConfigService } from '@wilson/config';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { UserFiltersSettingsState } from '@wilson/filter';
import { provideFormDrawer } from '@wilson/forms';
import { GeneralCompanySettingsState } from '@wilson/general-company-settings';
import { ActivityCategoriesState } from '@wilson/activity-categories/data';
import { InvoicingStateModule } from '@wilson/invoicing/state';
import { storageAndAuthStateInitializer } from '@wilson/ionic/storage';
import { LabelsState } from '@wilson/labels';
import {
  LocalizationState,
  USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE,
  LocalStorageLanguagePreferenceStorage,
  ANGULAR_LOCALE_DATA,
} from '@wilson/localization';
import { LocationState } from '@wilson/locations';
import { MapboxConfigModule } from '@wilson/mapbox-config';
import { SentryMonitoringModule } from '@wilson/monitoring/sentry';
import {
  AG_GRID_CURRENT_USER_CONTEXT,
  AgGridColumnSettingsState,
  type AgGridCurrentUserContext,
} from '@wilson/non-domain-specific/ag-grid-custom/ag-grid-column-tree';
import { NotificationsModule } from '@wilson/notifications';
import { OperationsBoardSettingState } from '@wilson/operations/state';
import { OperativeReportsState } from '@wilson/operative-reports/state';
import { OrganizationalUnitsState } from '@wilson/organizational-unit';
import {
  LocationPreferencesState,
  PreferenceSettingsState,
  ShiftTimePreferenceSettingState,
  UserGeneralPreferencesState,
  VehiclePreferencesState,
} from '@wilson/preferences/state';
import {
  FilterSettingsState,
  ShiftDrawerSettingsState,
  ShiftTimelinePreferredSettingsState,
} from '@wilson/preferred-settings/state';
import { ProfessionState } from '@wilson/profession/core';
import { ProfitabilityScoreState } from '@wilson/profitability-score/state';
import { ProjectsState } from '@wilson/projects/state';
import { QualificationSettingsModule } from '@wilson/qualification-settings';
import {
  QualificationsV2CategoriesState,
  QualificationsV2State,
} from '@wilson/qualification-v2/common';
import { QualificationsModule } from '@wilson/qualifications';
import { RouteModule } from '@wilson/route';
import { SectorState } from '@wilson/sectors';
import { NewWilsonJobBucketState } from '@wilson/share/state';
import { ShiftCategoriesState } from '@wilson/shift-category';
import { ShiftPlanStateModule } from '@wilson/shift-plan/state';
import { ShiftTemplatesState } from '@wilson/shift-templates';
import {
  ShiftAssignmentModalState,
  ShiftTimelineSettingsState,
  ShiftTimelineStateModule,
} from '@wilson/shift-timeline/state';
import { ShiftsModule } from '@wilson/shifts';
import { SideNavSettingsState } from '@wilson/side-nav-settings/state';
import { TalkJsConfigToken } from '@wilson/talkjs/services';
import { TitleManagerModule } from '@wilson/title-manager';
import { de as deDE } from 'date-fns/locale';
import { NZ_ICONS } from 'ng-zorro-antd/icon';
import { DateFnsModule } from 'ngx-date-fns';
import { MatomoModule, MatomoRouterModule } from 'ngx-matomo-client';
import { withNgxsResetPlugin } from 'ngxs-reset-plugin';
import { environment } from '../environments/environment';
import { REALTIME_WS_URL } from '@wilson/non-domain-specific/realtime-collaboration/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DatenschutzComponent } from './datenschutz/datenschutz.component';
import { ImpressumComponent } from './impressum/impressum.component';
import { JwtOptionsFactory } from './jwt-options.factory';
import { SharedModule } from './shared/shared.module';
import { BrowserRefreshState } from '@wilson/user-data/state';
import { provideNzDateFnsAdapter } from 'ng-zorro-antd/core/time';

Object.values(ANGULAR_LOCALE_DATA).forEach((locale) => {
  registerLocaleData(locale);
});
@NgModule({
  declarations: [AppComponent, DatenschutzComponent, ImpressumComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgbModule,
    IonicStorageModule.forRoot({
      name: '__portaldb',
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
    }),
    NgxsModule.forRoot(
      [
        AuthState,
        AbsenceCategoriesState,
        LocalizationState,
        LocationState,
        ShiftTimelinePreferredSettingsState,
        ShiftDrawerSettingsState,
        LocationPreferencesState,
        UserGeneralPreferencesState,
        PreferenceSettingsState,
        ProfessionState,
        VehiclePreferencesState,
        ShiftTimePreferenceSettingState,
        ShiftAssignmentModalState,
        LabelsState,
        ActivityCategoriesState,
        OperativeReportsState,
        ProjectsState,
        ProfitabilityScoreState,
        OperationsBoardSettingState,
        SideNavSettingsState,
        BrowserRefreshState,
        ShiftTimelineSettingsState,
        UsersState,
        OrganizationalUnitsState,
        AgGridColumnSettingsState,
        UserFiltersSettingsState,
        FilterSettingsState,
        GeneralCompanySettingsState,
        BankDetailsState,
        ClientsState,
        ExternalUsersState,
        QualificationsV2CategoriesState,
        QualificationsV2State,
        SectorState,
        ShiftCategoriesState,
        ShiftTemplatesState,
      ],
      {
        developmentMode: !environment.production,
        selectorOptions: {
          injectContainerState: false,
        },
      },
    ),
    NgxsStoragePluginModule.forRoot({
      keys: [
        // LocalizationState omitted: user preference is stored via USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE (portal) so we can distinguish user vs system language
        LocationState,
        ShiftTimelinePreferredSettingsState,
        ShiftDrawerSettingsState,
        OperationsBoardSettingState,
        NewWilsonJobBucketState,
        SideNavSettingsState,
        ShiftTimelineSettingsState,
        AgGridColumnSettingsState,
        UserFiltersSettingsState,
        FilterSettingsState,
        ExternalUsersState,
        QualificationsV2CategoriesState,
        QualificationsV2State,
      ],
    }),
    ShiftTimelineStateModule,
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
    NgxsFormPluginModule.forRoot(),
    QualificationsModule,
    QualificationSettingsModule,
    SharedModule,
    ConfigModule.forRoot({
      host: environment.backend.host,
      application: environment.application,
    }),
    MapboxConfigModule.forRoot({
      token: environment.mapboxToken,
    }),
    FeatureFlagsModule.forRoot({
      clientId: environment.launchDarklyClientId,
    }),
    AuthCoreModule,
    AccountModule,
    ShiftsModule,
    ActivitiesModule,
    AssignmentModule,
    AuthorizationModule,
    DateFnsModule.forRoot(),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: JwtOptionsFactory,
        deps: [Store, ConfigService],
      },
    }),
    BrowserAnimationsModule,
    NotificationsModule,
    FormsModule,
    RouteModule,
    TitleManagerModule,
    MatomoModule.forRoot({
      disabled: environment.matomo.disabled,
      trackerUrl: environment.matomo.trackerUrl,
      siteId: environment.matomo.siteId,
    }),
    MatomoRouterModule.forRoot({
      delay: 100,
      exclude: /\/shift-timeline\/?.*/,
    }),
    InvoicingStateModule,
    ShiftPlanStateModule,
    ShiftTimelineStateModule,
    SentryMonitoringModule,
  ],
  providers: [
    provideTranslateService({ loader: provideTranslateHttpLoader() }),
    // CDK 21 renders overlays in the browser top layer (native popover) by default, which
    // ignores z-index and changes how ng-zorro overlays stack with other layers. Keep the
    // 20.x behaviour (the CDK already falls back to it where popover isn't supported).
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideAppInitializer(() => {
      const initializerFn = storageAndAuthStateInitializer(
        inject(Storage),
        inject(Store),
      );
      return initializerFn();
    }),
    // AuthInterceptor reads the Authorization header JwtInterceptor attaches, so JwtInterceptor
    // must run first: keep JwtModule.forRoot in imports, which registers it ahead of this.
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PermissionInterceptor,
      multi: true,
    },
    {
      provide: TalkJsConfigToken,
      useValue: environment.talkJsConfig,
    },
    {
      provide: NZ_ICONS,
      useValue: [DownloadOutline, PlusOutline, LoadingOutline, AppstoreOutline],
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    withNgxsResetPlugin(),
    provideFormDrawer(),
    {
      provide: USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE,
      useClass: LocalStorageLanguagePreferenceStorage,
    },
    {
      provide: AG_GRID_CURRENT_USER_CONTEXT,
      deps: [Store],
      useFactory: (store: Store): AgGridCurrentUserContext => ({
        getCurrentUserId: () => store.selectSnapshot(AuthState.userId),
      }),
    },
    {
      provide: REALTIME_WS_URL,
      useValue: environment.realtime?.wsUrl ?? '',
    },
    // The locale is relevant at the beginning of the app lifecycle, before the localization state is initialized
    provideNzDateFnsAdapter({ locale: deDE }),
  ],
})
export class AppModule {}
